import IOSearch from "@interopio/search-api";
import IOWorkspaces from "@interopio/workspaces-api";
import { AppsClientImpl } from "./apps/index";
import { AuthenticationClientImpl } from "./authentication/index";
import { ConfigClientImpl } from "./config/index";
import { DialogManagerClientImpl } from "./dialogManager/index";
import { DistributedStoreClientImpl } from "./distributedStore/index";
import { Fin } from "./fin/fin";
import { SystemImpl } from "./system";
import { FinsembleWindowImpl } from "./finsembleWindow/index";
import { HotkeyClientImpl } from "./hotkey/index";
import { LoggerImpl } from "./logger/index";
import { NotificationClientImpl } from "./notification/index";
import { Router } from "./router/router";
import { SearchClientImpl } from "./search/index";
import { StorageClientImpl } from "./storage/index";
import { WindowClientImpl } from "./window/index";
import { WorkspaceClientImpl } from "./workspace/index";
import { SystemManagerClientImpl } from "./systemManager/index";
import { ToastImpl } from "./toast/index";
import { AsyncConstructors, FINSEMBLECONFIG, safeInteropRegister, getConfigurationAndCache } from "./common/windowFunctions";
import { maybeInjectThemeCSS } from "./theme";
import { BloombergBridgeClientImpl } from "./bloomberg";
import { PromiseWrapper } from "./utils/pw";
import { monkeyPatchFDC3Initialize, monkeyPatchFDC3Start } from "./fdc3";
const performanceStart = new Date().getTime();
if (globalThis.window) {
    console.debug("FSBL preload running");
    const glue42gd = globalThis?.glue42gd;
    if (glue42gd) {
        glue42gd.gwURL = glue42gd.gwURL.replace("localhost", "127.0.0.1");
    }
}
const defaultGlueFactoryConfig = {
    appManager: "full",
    layouts: {
        mode: "full",
        autoSaveWindowContext: [
            "Workspace",
            "Global"
        ]
    },
    windows: true,
    bus: true,
    channels: true,
    contexts: true,
    libraries: [
        IOSearch,
        IOWorkspaces
    ]
};
const maybeHideSSOApplication = async (glue) => {
    const config = await getConfigurationAndCache(glue.windows.my());
    if (config?.details.hidden) {
        glue.windows.my()?.hide();
    }
};
const startHeartbeats = async (glue) => {
    const win = glue.windows.my();
    if (!win) {
        return;
    }
    const { id, name } = win;
    setInterval(() => {
        glue.interop.invoke("Finsemble.heartbeat", {
            type: "component",
            windowName: `${name}:${id}`,
            componentType: "finsemble",
            isBrowserView: true
        });
    }, 10000);
};
export const initializeFinsembleGlobals = (glueFactory, config) => {
    monkeyPatchFDC3Initialize();
    const ready = new PromiseWrapper();
    const glue = glueFactory(config ?? defaultGlueFactoryConfig);
    const asyncConstructors = new AsyncConstructors();
    const Logger = new LoggerImpl(glue, asyncConstructors);
    const router = new Router(glue, asyncConstructors, Logger);
    Logger.initialize(router);
    FinsembleWindowImpl.setStaticInstances(glue, Logger);
    const fin = new Fin(glue, asyncConstructors, Logger);
    const appsClient = new AppsClientImpl(glue, asyncConstructors, router, Logger);
    const distributedStoreClient = new DistributedStoreClientImpl(glue, asyncConstructors, Logger);
    const finsembleWindow = new FinsembleWindowImpl(glue, asyncConstructors);
    const windowClient = new WindowClientImpl(glue, asyncConstructors, Logger);
    const storageClient = new StorageClientImpl(glue, asyncConstructors, Logger);
    const authenticationClient = new AuthenticationClientImpl(glue, asyncConstructors, Logger, router, storageClient);
    const systemClient = new SystemImpl(glue, asyncConstructors, Logger);
    let autoSave = true;
    const addEventListener = async (listenerType, callback) => {
        if (listenerType === "onReady") {
            await ready.promise;
            callback();
        }
    };
    const configClient = new ConfigClientImpl(glue, asyncConstructors, router, Logger);
    const FSBL = {
        addEventListener,
        Clients: {
            AppsClient: appsClient,
            AuthenticationClient: authenticationClient,
            DialogManager: new DialogManagerClientImpl(glue, asyncConstructors, router, appsClient, distributedStoreClient, Logger),
            SearchClient: new SearchClientImpl(glue, asyncConstructors, Logger),
            RouterClient: router,
            WorkspaceClient: new WorkspaceClientImpl(glue, asyncConstructors, Logger),
            ConfigClient: configClient,
            LauncherClient: appsClient,
            WindowClient: windowClient,
            DistributedStoreClient: distributedStoreClient,
            StorageClient: storageClient,
            HotkeyClient: new HotkeyClientImpl(glue, asyncConstructors, Logger),
            NotificationClient: new NotificationClientImpl(glue, asyncConstructors, Logger),
            Logger,
            BloombergBridgeClient: new BloombergBridgeClientImpl(glue, asyncConstructors, Logger),
        },
        SystemManagerClient: new SystemManagerClientImpl(glue, asyncConstructors, Logger),
        getFSBLInfo: async (cb) => {
            const info = await systemClient.getFEAInfo();
            const result = {
                FSBLVersion: info["io.Connect Desktop"],
                gitHash: "",
                "io.Connect Desktop": info["io.Connect Desktop"],
                "@interopio/desktop": info["@interopio/desktop"],
                Electron: info.Electron,
                FEA: info.FEA,
                isDevLaunch: false,
                isDevMode: false,
                sddEnabled: false,
            };
            cb?.(result);
            return result;
        },
        UserNotification: new ToastImpl(glue),
        componentType: "",
        publishReady: () => {
            glue.then((g) => {
                g.interop.invoke("T42.GD.Execute", { command: "signal-app-ready" });
            });
        },
        restartApplication: async (restartParams) => (await glue).appManager.restart({
            autoSave,
            showDialog: restartParams?.forceRestart ?? false
        }),
        shutdownApplication: () => {
            glue.then((g) => {
                g.appManager.exit({
                    autoSave,
                    showDialog: false
                });
            });
        },
        onShutdown: () => {
            return;
        },
        System: systemClient,
        glue,
        version: "0.0.1",
        FinsembleWindow: FinsembleWindowImpl
    };
    console.debug("FSBL preload: Glue API initializing");
    Logger.debug("FSBL preload: Glue API initializing");
    glue.then(async (g) => {
        console.debug("FSBL preload: Glue API ready");
        Logger.debug("FSBL preload: Glue API ready");
        if (g.windows.my()?.name === "Toolbar") {
            g.interop.invoke("T42.GD.Execute", { command: "signal-app-ready" });
        }
        await Promise.all([asyncConstructors.ready()]);
        Logger.debug("FSBL preload: All clients are ready");
        globalThis.iop = globalThis.iop ?? g;
        const componentType = g.windows?.my()?.application?.type;
        if (componentType) {
            FSBL.componentType = componentType;
        }
        g.windows?.my()?.onRefreshing(async (prevent) => {
            const manifest = await g.contexts.get(FINSEMBLECONFIG);
            if (manifest?.["finsemble-electron-adapter"]?.reloadWindowHotkey === false) {
                prevent();
            }
        });
        g.windows?.my()?.onFocusChanged((params) => {
            if (params.isFocused) {
                document.documentElement.classList.add("desktop-active");
            }
            else {
                document.documentElement.classList.remove("desktop-active");
            }
        });
        if (g.windows?.my()?.name === "sso-application") {
            g.interop.register("Finsemble.Clients.AuthenticationClient.publishAuthorization.backChannel", safeInteropRegister(async (params, instance) => {
                console.debug("Received backChannel credentials from application", instance.applicationName);
                authenticationClient.publishAuthorization(params?.user, params?.credentials);
            }));
            maybeHideSSOApplication(g);
            router.publish("AuthorizationState", { state: "starting" }).then(() => {
                router.publish("AuthorizationState", { state: "waiting" });
            });
        }
        g.bus.subscribe("WorkspaceService.ignoreDirtyWorkspace", () => {
            autoSave = false;
        });
        maybeInjectThemeCSS(g);
        monkeyPatchFDC3Start(g, Logger);
        Logger.debug(`FSBL preload ready ${new Date().getTime() - performanceStart}ms`);
        ready.resolve();
        const event = new Event("FSBLReady");
        globalThis.dispatchEvent?.(event);
        startHeartbeats(g);
    });
    return {
        FSBL,
        finsembleWindow,
        fin
    };
};
